/**
 * Client for an event's attendee rows, `/api/events/{event_id}/attendees` (#887): EventDetail's list, add,
 * status change and remove all go through it. Two reads go the other way round (#889): `listContactEvents`
 * is one contact's events (`GET /api/contacts/{id}/events`) and `getContact` is one contact's row on one
 * event (`GET /api/events/{event_id}/contacts/{contact_id}`).
 *
 * A status change sends `expected_state_version`, the row's `state_version` as last read (a missing one
 * counts as 0). If someone else changed the row since, the server answers 409 `event_contact_conflict`
 * with a `message` naming who did, `current` (the row as it is now) and `current_state_version`;
 * `isEventContactConflict` recognises it. The PUT answers `{ok, attendee}`, where `attendee` is the wire
 * row WITHOUT the joined `contact` block that the list carries: merge it into the row, never replace it.
 *
 * DELETE archives the row (a second DELETE is 404); re-adding the contact restores it, and the add
 * response counts those under `restored`. A status change on a removed row is 409
 * `event_contact_archived` (`isEventContactArchived`); on a deleted event it is 404 "Event not found".
 */
import axios from 'axios';

// `|| ''`: an unset URL means same origin (`/api/...`), never `undefined/api/...`.
const base = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/events`;
const contactsBase = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/contacts`;

/** The code of a 409 refusal, or null. Reads `code`, then `error` (#887 P18 sends both; `code` wins). */
function conflictCode(err) {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status !== 409 || detail == null || typeof detail !== 'object') return null;
    return detail.code ?? detail.error ?? null;
}

/** True for the 409 a stale `expected_state_version` gets. */
export function isEventContactConflict(err) {
    return conflictCode(err) === 'event_contact_conflict';
}

/** True for the 409 a change to a removed (archived) attendee gets. */
export function isEventContactArchived(err) {
    return conflictCode(err) === 'event_contact_archived';
}

export function createEventContactsApi(getAuthHeader) {
    const cfg = (extra) => ({ headers: getAuthHeader ? getAuthHeader() : {}, ...extra });
    const id = (value) => encodeURIComponent(value);
    const attendees = (eventId) => `${base()}/${id(eventId)}/attendees`;
    const data = (res) => res.data;
    return {
        /** `{data: [attendee…], total, page, page_size}`; `params` are `status`, `search`, `page`, `page_size`. */
        listAttendees: (eventId, params) => axios.get(attendees(eventId), cfg({ params })).then(data),
        /** `body` supports workflow enrollment and optional per-contact outcomes; answers aggregate counts. */
        addAttendees: (eventId, body) => axios.post(attendees(eventId), body, cfg()).then(data),
        /** Create/reuse a contact and check the person in through the canonical writer. */
        addWalkIn: (eventId, body) => axios.post(`${base()}/${id(eventId)}/walk-ins`, body, cfg()).then(data),
        /** Answers `{ok, attendee}`. Only the fields given are sent. */
        updateAttendee: (eventId, attendeeId, { status, notes, expected_state_version, operation_id } = {}) => {
            const body = {};
            if (status !== undefined) body.status = status;
            if (notes !== undefined) body.notes = notes;
            if (expected_state_version !== undefined) body.expected_state_version = expected_state_version;
            if (operation_id !== undefined) body.operation_id = operation_id;
            return axios.put(`${attendees(eventId)}/${id(attendeeId)}`, body, cfg()).then(data);
        },
        /** Record a guest-list reminder with actor/timestamp audit metadata. */
        sendReminder: (eventId, attendeeId) =>
            axios.post(`${attendees(eventId)}/${id(attendeeId)}/reminder`, {}, cfg()).then(data),

        /** Canonical event-contact transition writer. */
        transitionContact: (eventId, contactId, body) =>
            axios.patch(`${base()}/${id(eventId)}/contacts/${id(contactId)}`, body, cfg()).then(data),
        /** Archives the row; answers `{ok: true}`. */
        removeAttendee: (eventId, attendeeId) =>
            axios.delete(`${attendees(eventId)}/${id(attendeeId)}`, cfg()).then(data),
        /** Claim an unowned contact through the audited self-assignment route. */
        claimContact: (contactId) => axios.post(`${contactsBase()}/${id(contactId)}/claim`, {}, cfg()).then(data),

        /**
         * One contact's events, newest event first: `{events: [row…], next_cursor, truncated, total}`.
         * `params` are `limit` (1-100), `cursor` (the previous page's `next_cursor`) and `include_archived`.
         * `withDetails: false` asks for the rows without the summary reads (timing, follow-up, contributors,
         * session counts), for a caller that only needs which events the contact is on; it is sent as
         * `with_details=false` and only when false, so the default request is unchanged.
         * A hidden, unknown or deleted contact is 404 `contact_not_found`.
         */
        listContactEvents: (contactId, { withDetails, ...params } = {}) =>
            axios.get(`${contactsBase()}/${id(contactId)}/events`, cfg({
                params: withDetails === false ? { ...params, with_details: false } : params,
            })).then(data),
        /**
         * One contact's state on one event: `{attendee, event, workflow}`. `workflow` is null while the row has
         * no stage. 404 `event_contact_not_found` when the contact is not on the event.
         */
        getContact: (eventId, contactId) =>
            axios.get(`${base()}/${id(eventId)}/contacts/${id(contactId)}`, cfg()).then(data),
    };
}
